import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useQueryParams from 'hooks/useQueryParams'
import queryString from 'query-string'
import { useMemo } from 'react'
import { isUserOfRole } from 'utils/authUtils'

export const CopilotReports = {
  Copilot: 'copilot',
  OralHygiene: 'oralHygiene',
  TxTracking: 'txTracking'
}

/**
 * A report path that keeps an internal user's doctor override. Without it the destination report
 * falls back to the signed-in account and its guard can redirect again.
 */
export const buildCopilotReportPath = (route, copilotDoctorId) =>
  copilotDoctorId ? `${route}?${queryString.stringify({ copilotDoctorId })}` : route

/**
 * The Copilot reports the selected account owner can open, in rail order.
 * Each report sits behind its own practice feature flag. Copilot and Tx Tracking also require an
 * HI plan; Oral Hygiene does not (GR-10760), so a non-HI account with only that flag gets a
 * single-report Copilot area. A DevAdmin override skips the plan requirement, like the report
 * guards do, because the plan resolved here is the admin's context and not the overridden practice's.
 */
export default () => {
  const {
    copilotControl: copilotControlFF,
    oralHygieneReport: oralHygieneReportFF,
    txTrackingReport: txTrackingReportFF
  } = useFeatureFlags()
  const { isHI } = usePlanLimits()
  const { queryParams } = useQueryParams()
  const isDevAdminOverride = isUserOfRole([Roles.DevAdmin]) && !!queryParams.copilotDoctorId
  const meetsHIRequirement = !!isHI || isDevAdminOverride

  return useMemo(
    () =>
      [
        {
          key: CopilotReports.Copilot,
          route: ROUTES.COPILOT_CONTROL,
          isEnabled: copilotControlFF && meetsHIRequirement
        },
        { key: CopilotReports.OralHygiene, route: ROUTES.COPILOT_ORAL_HYGIENE, isEnabled: oralHygieneReportFF },
        {
          key: CopilotReports.TxTracking,
          route: ROUTES.COPILOT_TX_TRACKING,
          isEnabled: txTrackingReportFF && meetsHIRequirement
        }
      ].filter(report => !!report.isEnabled),
    [copilotControlFF, oralHygieneReportFF, txTrackingReportFF, meetsHIRequirement]
  )
}
